import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Palette, Users, Target, ShieldCheck, Briefcase } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { eur, pct } from "../../lib/format";
import { useViewRole } from "../../context/ViewRoleContext";
import { useAuth } from "../../context/AuthContext";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";

export default function AdminView() {
  const { isAdmin } = useViewRole();

  if (!isAdmin) {
    return (
      <div className="rounded-xl bg-[#111827] border border-[#1E293B] p-10 text-center text-slate-400" data-testid="admin-restricted">
        <ShieldCheck className="h-8 w-8 mx-auto mb-3 text-slate-600" />
        Cette section est réservée au Directeur / Admin.
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-view">
      <div>
        <h1 className="font-head text-3xl font-extrabold tracking-tight">Administration</h1>
        <p className="text-slate-400 text-sm mt-1">Configurez les familles de produits et gérez vos vendeurs.</p>
      </div>
      <Tabs defaultValue="familles">
        <TabsList className="bg-[#111827] border border-[#1E293B]">
          <TabsTrigger value="familles" data-testid="admin-tab-familles" className="data-[state=active]:bg-amber-500 data-[state=active]:text-[#0B0F17]">
            <Palette className="h-4 w-4 mr-1.5" /> Familles de produits
          </TabsTrigger>
          <TabsTrigger value="vendeurs" data-testid="admin-tab-vendeurs" className="data-[state=active]:bg-amber-500 data-[state=active]:text-[#0B0F17]">
            <Users className="h-4 w-4 mr-1.5" /> Vendeurs
          </TabsTrigger>
        </TabsList>
        <TabsContent value="familles" className="mt-5"><FamillesTab /></TabsContent>
        <TabsContent value="vendeurs" className="mt-5"><VendeursTab /></TabsContent>
      </Tabs>
    </div>
  );
}

const EMPTY_FAM = { nom: "", couleur: "#38BDF8" };

function FamillesTab() {
  const [familles, setFamilles] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FAM);
  const [editId, setEditId] = useState(null);

  const load = () => api.get("/familles").then((r) => setFamilles(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm(EMPTY_FAM); setEditId(null); setOpen(true); };
  const openEdit = (f) => { setForm({ nom: f.nom, couleur: f.couleur }); setEditId(f.id); setOpen(true); };

  const save = async () => {
    if (!form.nom) return toast.error("Nom requis");
    try {
      if (editId) { await api.put(`/familles/${editId}`, form); toast.success("Famille mise à jour"); }
      else { await api.post("/familles", form); toast.success("Famille ajoutée"); }
      setOpen(false); load();
    } catch { toast.error("Erreur"); }
  };

  const remove = async (id) => {
    try { await api.delete(`/familles/${id}`); toast.success("Famille supprimée"); load(); }
    catch { toast.error("Erreur"); }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button data-testid="add-famille-button" onClick={openNew} className="bg-amber-500 hover:bg-amber-600 text-[#0B0F17] font-semibold">
          <Plus className="h-4 w-4 mr-1" /> Nouvelle famille
        </Button>
      </div>
      <div className="rounded-xl bg-[#111827] border border-[#1E293B] overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wider text-slate-500 border-b border-[#1E293B]">
              <th className="px-4 py-3 font-medium">Couleur</th>
              <th className="px-3 py-3 font-medium">Famille de produits</th>
              <th className="px-3 py-3 font-medium text-right"></th>
            </tr>
          </thead>
          <tbody>
            {familles.map((f) => (
              <tr key={f.id} className="border-b border-[#1E293B]/60 hover:bg-[#0B0F17]/50" data-testid={`famille-row-${f.id}`}>
                <td className="px-4 py-3"><span className="inline-block h-5 w-5 rounded" style={{ background: f.couleur }} /></td>
                <td className="px-3 py-3 font-medium">{f.nom}</td>
                <td className="px-3 py-3 text-right whitespace-nowrap">
                  <button data-testid={`famille-edit-${f.id}`} onClick={() => openEdit(f)} className="text-slate-400 hover:text-amber-400 p-1"><Pencil className="h-4 w-4" /></button>
                  <button data-testid={`famille-delete-${f.id}`} onClick={() => remove(f.id)} className="text-slate-400 hover:text-red-400 p-1 ml-1"><Trash2 className="h-4 w-4" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#111827] border-[#1E293B] text-slate-100">
          <DialogHeader><DialogTitle className="font-head">{editId ? "Modifier la famille" : "Nouvelle famille"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label className="text-slate-400 text-xs">Nom de la famille</Label>
              <Input data-testid="famille-input-nom" value={form.nom} onChange={(e) => setForm({ ...form, nom: e.target.value })} className="bg-[#0B0F17] border-[#1E293B]" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-slate-400 text-xs">Couleur</Label>
              <div className="flex items-center gap-3">
                <input type="color" data-testid="famille-input-couleur" value={form.couleur} onChange={(e) => setForm({ ...form, couleur: e.target.value })} className="h-10 w-16 bg-[#0B0F17] border border-[#1E293B] rounded cursor-pointer" />
                <span className="font-mono-metric text-sm text-slate-400">{form.couleur}</span>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)} className="text-slate-400">Annuler</Button>
            <Button data-testid="famille-save-button" onClick={save} className="bg-amber-500 hover:bg-amber-600 text-[#0B0F17] font-semibold">Enregistrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const EMPTY_USER = { name: "", email: "", password: "", role: "commercial", objectif_ca: "" };

function VendeursTab() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_USER);
  const [editId, setEditId] = useState(null);

  const load = () => api.get("/users").then((r) => setUsers(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm(EMPTY_USER); setEditId(null); setOpen(true); };
  const openEdit = (u) => { setForm({ name: u.name, email: u.email, password: "", role: u.role, objectif_ca: u.objectif_ca || "" }); setEditId(u.id); setOpen(true); };

  const save = async () => {
    if (!form.name) return toast.error("Nom requis");
    try {
      if (editId) {
        await api.put(`/users/${editId}`, { name: form.name, role: form.role, objectif_ca: Number(form.objectif_ca) || 0, password: form.password || null });
        toast.success("Vendeur mis à jour");
      } else {
        if (!form.email || !form.password) return toast.error("Email et mot de passe requis");
        await api.post("/users", { name: form.name, email: form.email, password: form.password, role: form.role, objectif_ca: Number(form.objectif_ca) || 0 });
        toast.success("Vendeur invité");
      }
      setOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const remove = async (id) => {
    try { await api.delete(`/users/${id}`); toast.success("Vendeur supprimé"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button data-testid="add-user-button" onClick={openNew} className="bg-amber-500 hover:bg-amber-600 text-[#0B0F17] font-semibold">
          <Plus className="h-4 w-4 mr-1" /> Inviter un vendeur
        </Button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {users.map((u) => {
          const obj = u.objectif_ca || 0;
          const prog = obj ? Math.min(100, (u.ca_realise / obj) * 100) : 0;
          return (
            <div key={u.id} className="rounded-xl bg-[#111827] border border-[#1E293B] p-5" data-testid={`user-card-${u.id}`}>
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className={`h-10 w-10 rounded-full flex items-center justify-center ${u.role === "admin" ? "bg-amber-500/15 text-amber-400" : "bg-sky-500/15 text-sky-400"}`}>
                    {u.role === "admin" ? <ShieldCheck className="h-5 w-5" /> : <Briefcase className="h-5 w-5" />}
                  </div>
                  <div>
                    <div className="font-semibold">{u.name}</div>
                    <div className="text-xs text-slate-500">{u.email}</div>
                  </div>
                </div>
                <span className={`text-[11px] font-semibold px-2 py-0.5 rounded border ${u.role === "admin" ? "bg-amber-500/10 text-amber-400 border-amber-500/20" : "bg-sky-500/10 text-sky-400 border-sky-500/20"}`}>
                  {u.role === "admin" ? "Admin" : "Commercial"}
                </span>
              </div>
              {u.role === "commercial" && (
                <div className="mt-4">
                  <div className="flex justify-between text-xs mb-1.5">
                    <span className="text-slate-500 inline-flex items-center gap-1"><Target className="h-3.5 w-3.5" /> Objectif CA</span>
                    <span className="font-mono-metric text-slate-300">{eur(u.ca_realise)} / {eur(obj)}</span>
                  </div>
                  <div className="h-2 rounded-full bg-[#0B0F17] overflow-hidden">
                    <div className={`h-full rounded-full ${prog >= 80 ? "bg-emerald-500" : prog >= 40 ? "bg-amber-500" : "bg-sky-500"}`} style={{ width: `${prog}%` }} />
                  </div>
                  <div className="mt-1 text-right text-[11px] text-slate-500 font-mono-metric">{pct(prog)} atteint</div>
                </div>
              )}
              <div className="flex justify-end gap-1 mt-3 pt-3 border-t border-[#1E293B]">
                <button data-testid={`user-edit-${u.id}`} onClick={() => openEdit(u)} className="text-slate-400 hover:text-amber-400 p-1"><Pencil className="h-4 w-4" /></button>
                {u.id !== user?.id && (
                  <button data-testid={`user-delete-${u.id}`} onClick={() => remove(u.id)} className="text-slate-400 hover:text-red-400 p-1"><Trash2 className="h-4 w-4" /></button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#111827] border-[#1E293B] text-slate-100">
          <DialogHeader><DialogTitle className="font-head">{editId ? "Modifier le vendeur" : "Inviter un vendeur"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label className="text-slate-400 text-xs">Nom complet</Label>
              <Input data-testid="user-input-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-[#0B0F17] border-[#1E293B]" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-slate-400 text-xs">Email</Label>
              <Input data-testid="user-input-email" type="email" disabled={!!editId} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="bg-[#0B0F17] border-[#1E293B] disabled:opacity-50" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-slate-400 text-xs">{editId ? "Nouveau mot de passe (option.)" : "Mot de passe"}</Label>
                <Input data-testid="user-input-password" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="bg-[#0B0F17] border-[#1E293B]" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-slate-400 text-xs">Rôle</Label>
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                  <SelectTrigger className="bg-[#0B0F17] border-[#1E293B]"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#111827] border-[#1E293B] text-slate-100">
                    <SelectItem value="commercial">Commercial</SelectItem>
                    <SelectItem value="admin">Admin</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-slate-400 text-xs">Objectif de CA annuel (€)</Label>
              <Input data-testid="user-input-objectif" type="number" value={form.objectif_ca} onChange={(e) => setForm({ ...form, objectif_ca: e.target.value })} className="bg-[#0B0F17] border-[#1E293B] font-mono-metric" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)} className="text-slate-400">Annuler</Button>
            <Button data-testid="user-save-button" onClick={save} className="bg-amber-500 hover:bg-amber-600 text-[#0B0F17] font-semibold">Enregistrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
